/**
 * E002-T039: FieldMappingGrid Component
 * E002-T043: Added type compatibility indicators
 *
 * Grid for mapping workflow engine response fields to target table columns.
 * Each target column has a dropdown to select a response field.
 * Shows compatibility status: compatible, needs cast, incompatible
 */

import { Checkbox, notify, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { InformationIcon } from '@blocksuite/icons/rc';
import {
  FILE_INDEX_COLUMN_IDS,
  FILE_INDEX_DATABASE_ID_PREFIX,
  FILE_INDEX_MANAGED_BY,
} from '@streamlain/database-identity';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EditorService } from '../../../editor';
import { WorkbenchService } from '../../../workbench/services/workbench';
import type {
  MatchConfigProps,
  MultiMatchBehavior,
  OutputMapping,
  OutputTable,
  RelationMatchMode,
  StealBehavior,
  TargetMatchMode,
} from '../../services/automation';
import {
  type ColumnInfo,
  DatabaseDiscoveryService,
} from '../../services/database-discovery';
import { FullscreenRegistry } from '../../services/fullscreen-registry';
import { WorkflowService } from '../../services/workflow';
import { AutoMatchDialog } from './auto-match-dialog';
import { CollapsibleSection } from './collapsible-section';
import {
  MATCH_MODE_WITH_AUTO_OPTIONS,
  MatchConfigEditor,
} from './match-config-editor';
import { applyMappingChange } from './output-mapping-writer';
import {
  SectionEmpty,
  SectionMappingHeader,
  SectionMappingRow,
  SectionNotice,
  SectionStack,
} from './section-layout';
import { TypeChip } from './type-chip';
import type { FieldOptionGroup } from './typed-field-select';
import { TypedFieldSelect } from './typed-field-select';

const AUTOMATION_MAPPING_LIMIT = 32;
const OUTPUT_MAPPING_GRID_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr) 16px 24px';

/** sl-mau5: Generate a simple unique ID for MatchConfigProps */
function generateId(): string {
  return `mc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isFileIndexDatabaseId(databaseId: string | undefined): boolean {
  return (
    typeof databaseId === 'string' &&
    databaseId.startsWith(`${FILE_INDEX_DATABASE_ID_PREFIX}-`)
  );
}

function isFileIndexSystemColumnId(columnId: string | undefined): boolean {
  return (
    columnId === 'title' ||
    FILE_INDEX_COLUMN_IDS.includes(
      columnId as (typeof FILE_INDEX_COLUMN_IDS)[number]
    )
  );
}

function isFileIndexManagedColumnData(
  data: Record<string, unknown> | undefined
): boolean {
  return (
    data?.managedBy === FILE_INDEX_MANAGED_BY || data?.systemManaged === true
  );
}

interface FieldMappingGridProps {
  /** sl-4n0: Target table ID for output mappings */
  tableId?: string;
  /** sl-optc: Source table ID to detect input==output scenario */
  sourceTableId?: string;
  /** Target table columns */
  targetColumns: ColumnInfo[];
  /** Current mappings */
  mappings: OutputMapping[];
  /** Callback when mappings change */
  onMappingsChange: (mappings: OutputMapping[]) => void;
  /** sl-mau5: Match configurations (first-class objects) */
  matchConfigs?: MatchConfigProps[];
  /** sl-mau5: Callback when match configs change */
  onMatchConfigsChange?: (configs: MatchConfigProps[]) => void;
  /** sl-mau5: Output tables (for setting matchConfigId on table) */
  outputTables?: OutputTable[];
  /** sl-mau5: Callback when output tables change */
  onOutputTablesChange?: (tables: OutputTable[]) => void;
  /** Source table columns for resolving column IDs to names */
  sourceColumns?: ColumnInfo[];
  /** Skip the CollapsibleSection wrapper - use when already wrapped externally */
  noWrapper?: boolean;
}

/**
 * Build a map from column ID to column name
 */
function buildColumnIdToNameMap(columns: ColumnInfo[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const col of columns) {
    map.set(col.id, col.name);
  }
  return map;
}

/**
 * Resolve a field path like "payload.h1scAM_byy.value" to "ColumnName"
 * Handles payload prefix, resolves column IDs to names, removes .value suffix
 */
function resolveFieldPath(
  path: string,
  columnMap: Map<string, string>
): string {
  // Split path into segments
  const segments = path.split('.');

  // Find which segment might be a column ID
  // Could be first segment or second (after "payload")
  for (let i = 0; i < segments.length; i++) {
    const columnName = columnMap.get(segments[i]);
    if (columnName) {
      segments[i] = columnName;
      // Only resolve first match
      break;
    }
  }

  // Remove payload prefix for cleaner display
  if (segments[0] === 'payload') {
    segments.shift();
  }

  // Remove .value suffix for cleaner display (keep the actual path for data access)
  if (segments[segments.length - 1] === 'value') {
    segments.pop();
  }

  return segments.join('.');
}

function toFanoutItemFieldPath(value: string): string | null {
  const words = value
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return null;
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) return lower;
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join('');
}

function normalizeOutputFieldType(type: string): string {
  if (type === 'rich-text' || type === 'title') return 'text';
  if (type === 'relation') return 'text';
  return type;
}

// sl-1ahs.6.4: isYourDataField / shouldHideField / isSystemField helpers and
// the SYSTEM_FIELDS allow-list were removed together with the response-based
// field extraction path; the grid now derives fields exclusively from the
// workflow End node output schema.

/**
 * Type compatibility matrix
 * Returns: 'ok' (direct), 'cast' (needs conversion), 'error' (incompatible)
 */
function getTypeCompatibility(
  sourceType: string,
  targetType: string
): 'ok' | 'cast' | 'error' {
  // Normalize types
  const source = sourceType.toLowerCase().replace(/\[\d+\]/, '');
  const target = targetType.toLowerCase();

  // Direct compatibility
  if (source === target) return 'ok';

  // String can be cast to most types
  if (source === 'string') {
    if (['rich-text', 'text', 'title'].includes(target)) return 'ok';
    // sl-hih8: Relation columns are compatible — resolved via SmartMatchingService
    if (target === 'relation') return 'ok';
    if (['number', 'date', 'select', 'multi-select'].includes(target))
      return 'cast';
    return 'cast'; // Try anyway
  }

  // Number compatibility
  if (source === 'number') {
    if (['number', 'progress'].includes(target)) return 'ok';
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  // Boolean compatibility
  if (source === 'boolean') {
    if (target === 'checkbox') return 'ok';
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  // Array - usually incompatible except for multi-select
  if (source.startsWith('array')) {
    if (target === 'multi-select') return 'cast';
    return 'error';
  }

  // Object - usually incompatible
  if (source === 'object') {
    if (['string', 'rich-text', 'text'].includes(target)) return 'cast';
    return 'error';
  }

  return 'cast'; // Default: try casting
}

/**
 * S020-8.20.2: Check if column is a relation type
 */
function isRelationColumn(columnType: string): boolean {
  return columnType.toLowerCase() === 'relation';
}

// sl-qi4j.6: Match option constants moved to match-config-editor.tsx (DRY)

/**
 * sl-vb5r: Resolve a select option ID to its display label
 */
function resolveOptionLabel(column: ColumnInfo, optionId: string): string {
  const options =
    (column.data?.options as Array<{ id: string; value: string }>) ?? [];
  return options.find(o => o.id === optionId)?.value ?? optionId;
}

/**
 * Compatibility indicator colors and labels
 */
const COMPAT_CONFIG = {
  ok: { color: '#4CAF50', label: '✓', title: 'Compatible - direct mapping' },
  cast: {
    color: '#FF9800',
    label: '~',
    title: 'Needs conversion - will attempt type cast',
  },
  error: { color: '#F44336', label: '!', title: 'Incompatible types' },
};

export const FieldMappingGrid: FC<FieldMappingGridProps> = ({
  tableId,
  sourceTableId,
  targetColumns,
  mappings,
  onMappingsChange,
  matchConfigs = [],
  onMatchConfigsChange,
  outputTables = [],
  onOutputTablesChange,
  sourceColumns = [],
  noWrapper = false,
}) => {
  const t = useI18n();
  const [isAutoMatchOpen, setIsAutoMatchOpen] = useState(false);

  // Services for relation column features
  const discoveryService = useService(DatabaseDiscoveryService);
  const editorService = useServiceOptional(EditorService);
  const fullscreenRegistry = useServiceOptional(FullscreenRegistry);
  const workbenchService = useService(WorkbenchService);
  const allDatabases = useLiveData(discoveryService.blocks$);

  // sl-tq87: Schema fallback for output field mapping
  const workflowService = useService(WorkflowService);
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);

  // Resolve relation target database info (name + columns) by targetDatabaseId
  const getRelationTargetInfo = useCallback(
    (
      column: ColumnInfo
    ): {
      tableName: string;
      tableId: string;
      docId: string;
      columns: ColumnInfo[];
    } | null => {
      if (!column.targetDatabaseId) return null;
      const targetDb = allDatabases?.find(
        (db: { blockId: string }) => db.blockId === column.targetDatabaseId
      );
      if (!targetDb) return null;
      return {
        tableName: targetDb.databaseTitle,
        tableId: targetDb.blockId,
        docId: targetDb.docId,
        columns: targetDb.columns,
      };
    },
    [allDatabases]
  );

  // Navigate to relation target database in fullscreen
  const handleOpenRelationTarget = useCallback(
    (column: ColumnInfo) => {
      const targetDocId = column.targetDocId;
      const targetDbId = column.targetDatabaseId;
      if (!targetDbId) return;

      const currentDocId = editorService?.editor.doc.id;

      if (!targetDocId || targetDocId === currentDocId) {
        // Same doc: open fullscreen directly via FullscreenRegistry
        fullscreenRegistry?.open('database', targetDbId);
      } else {
        // Cross-doc: navigate to doc with fullscreen query param
        workbenchService.workbench.open(
          `/${targetDocId}?fullscreenDatabaseId=${targetDbId}`
        );
      }
    },
    [editorService, fullscreenRegistry, workbenchService]
  );

  // sl-mau5: Helper to find MatchConfig for a given table (row matching)
  const getRowMatchConfig = useCallback((): MatchConfigProps | undefined => {
    if (!tableId) return undefined;
    const table = outputTables.find(t => t.tableId === tableId);
    if (!table?.matchConfigId) return undefined;
    return matchConfigs.find(mc => mc.id === table.matchConfigId);
  }, [tableId, outputTables, matchConfigs]);

  // sl-mau5: Helper to find MatchConfig for a relation mapping
  const getRelationMatchConfig = useCallback(
    (targetColumnId: string): MatchConfigProps | undefined => {
      const mapping = mappings.find(m => m.targetColumnId === targetColumnId);
      if (!mapping?.matchConfigId) return undefined;
      return matchConfigs.find(mc => mc.id === mapping.matchConfigId);
    },
    [mappings, matchConfigs]
  );

  // sl-mau5: Check if a column is a match key (has an entry in the row MatchConfig)
  const isColumnMatchKey = useCallback(
    (targetColumnId: string): boolean => {
      const mc = getRowMatchConfig();
      if (!mc) return false;
      return mc.keys.some(k => k.columnId === targetColumnId);
    },
    [getRowMatchConfig]
  );

  // Handle lookupMatchColumn change for relation lookup
  const handleLookupMatchColumnChange = useCallback(
    (columnId: string, matchColumnId: string | undefined) => {
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId || !onMatchConfigsChange) return;
      const updatedConfigs = matchConfigs.map(mc => {
        if (mc.id === mapping.matchConfigId && mc.keys.length > 0) {
          const updatedKeys = [...mc.keys];
          updatedKeys[0] = {
            ...updatedKeys[0],
            columnId: matchColumnId ?? 'title',
          };
          return { ...mc, keys: updatedKeys };
        }
        return mc;
      });
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  // Build column ID to name map for resolving IDs
  const columnMap = useMemo(
    () => buildColumnIdToNameMap(sourceColumns),
    [sourceColumns]
  );

  // sl-czam: Map output field IDs to human-readable names from schema
  const schemaNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (workflowSchema?.output) {
      for (const [fieldId, def] of Object.entries(workflowSchema.output)) {
        const name = (def as { name?: string }).name;
        if (name) map.set(fieldId, name);
      }
    }
    return map;
  }, [workflowSchema]);

  // Helper to get display name for a field path
  const getDisplayName = useCallback(
    (path: string): string => {
      // Check schema name map first (field ID → human-readable name)
      const schemaName = schemaNameMap.get(path);
      if (schemaName) return schemaName;
      return resolveFieldPath(path, columnMap);
    },
    [columnMap, schemaNameMap]
  );

  // Available fields are derived exclusively from the workflow End node
  // output schema. sl-1ahs.6.4: the former response-based field extraction
  // branch was removed together with the cached lastResponse state; output
  // mapping correctness is now guaranteed at configure-time against this
  // schema (sl-1ahs.7.3).
  interface SchemaField {
    path: string;
    type: string;
    displayName: string;
  }
  const currentOutputTable = useMemo(
    () => outputTables.find(table => table.tableId === tableId),
    [outputTables, tableId]
  );
  const currentOutputTableIndex = useMemo(
    () => outputTables.findIndex(table => table.tableId === tableId),
    [outputTables, tableId]
  );
  const groupedFields = useMemo((): {
    workflowResponse: SchemaField[];
    fanoutItem: SchemaField[];
    targetContext: SchemaField[];
    source: 'schema' | 'none';
  } => {
    const fanoutItemFields: SchemaField[] = [];
    if (currentOutputTable?.sourceArrayField) {
      const seen = new Set<string>();
      const addFanoutField = (
        path: string | null,
        displayName: string,
        type: string
      ) => {
        if (!path || seen.has(path)) return;
        seen.add(path);
        fanoutItemFields.push({ path, displayName, type });
      };

      for (const column of targetColumns) {
        if (column.type === 'relation') continue;
        addFanoutField(
          toFanoutItemFieldPath(column.name),
          column.name,
          normalizeOutputFieldType(column.type)
        );
      }
      addFanoutField('item', 'Item', 'json');
      addFanoutField('value', 'Item value', 'json');
      addFanoutField('__index', 'Item index', 'number');
      addFanoutField('__item', 'Raw item', 'json');
    }

    const aliasSourceTables =
      currentOutputTableIndex > 0
        ? outputTables.slice(0, currentOutputTableIndex)
        : [];
    const targetContextFields = aliasSourceTables
      .filter(table => table.targetAlias)
      .flatMap(table => [
        {
          path: `__targets.${table.targetAlias}.rowId`,
          type: 'text',
          displayName: `${table.tableName || table.targetAlias} row`,
        },
        {
          path: `__targets.${table.targetAlias}.rowIds`,
          type: 'json',
          displayName: `${table.tableName || table.targetAlias} rows`,
        },
      ]);

    if (workflowSchema?.output) {
      const schemaFields: SchemaField[] = Object.entries(
        workflowSchema.output
      ).map(([fieldId, fieldDef]) => ({
        path: fieldId,
        type: (fieldDef as { type?: string }).type || 'text',
        displayName: (fieldDef as { name?: string }).name || fieldId,
      }));

      if (schemaFields.length > 0) {
        return {
          workflowResponse: schemaFields,
          fanoutItem: fanoutItemFields,
          targetContext: targetContextFields,
          source: 'schema',
        };
      }
    }

    // No schema available — empty catalog
    return {
      workflowResponse: [],
      fanoutItem: fanoutItemFields,
      targetContext: targetContextFields,
      source: 'none',
    };
  }, [
    currentOutputTable?.sourceArrayField,
    currentOutputTableIndex,
    outputTables,
    targetColumns,
    workflowSchema,
  ]);

  // Flat list for compatibility checks
  const responseFields = useMemo(
    () => [
      ...groupedFields.workflowResponse,
      ...groupedFields.fanoutItem,
      ...groupedFields.targetContext,
    ],
    [groupedFields]
  );

  const isFileIndexTable = useMemo(() => {
    return (
      isFileIndexDatabaseId(tableId) ||
      targetColumns.some(column => isFileIndexManagedColumnData(column.data))
    );
  }, [tableId, targetColumns]);

  const isFileIndexProtectedColumn = useCallback(
    (column: ColumnInfo) =>
      isFileIndexTable &&
      (isFileIndexSystemColumnId(column.id) ||
        isFileIndexManagedColumnData(column.data)),
    [isFileIndexTable]
  );

  const fileIndexOutputHint =
    'Files database rows are created from canvas files. Automations can update only user columns and must not create new rows.';

  // Build FieldOptionGroup[] for TypedFieldSelect
  const responseFieldGroups = useMemo((): FieldOptionGroup[] => {
    const groups: FieldOptionGroup[] = [];
    if (groupedFields.workflowResponse.length > 0) {
      groups.push({
        id: 'workflow-response',
        label: t['com.streamlain.streamlain.output.group-response'](),
        options: groupedFields.workflowResponse.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    if (groupedFields.fanoutItem.length > 0) {
      groups.push({
        id: 'fanout-item',
        label: t['com.streamlain.streamlain.output.group-fanout-item'](),
        options: groupedFields.fanoutItem.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    if (groupedFields.targetContext.length > 0) {
      groups.push({
        id: 'target-context',
        label: t['com.streamlain.streamlain.output.group-created-rows'](),
        options: groupedFields.targetContext.map(f => ({
          id: f.path,
          label: f.displayName || getDisplayName(f.path),
          type: f.type,
        })),
      });
    }
    return groups;
  }, [groupedFields, getDisplayName, t]);

  // Get current mapping for a column
  const getMappingForColumn = useCallback(
    (columnId: string): string => {
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      return mapping?.responseField ?? '';
    },
    [mappings]
  );

  // S020-8.20.2: Get full mapping object for a column
  const getFullMappingForColumn = useCallback(
    (columnId: string): OutputMapping | undefined => {
      return mappings.find(m => m.targetColumnId === columnId);
    },
    [mappings]
  );

  // Get response field info by path
  const getResponseFieldInfo = useCallback(
    (path: string) => {
      return responseFields.find(f => f.path === path);
    },
    [responseFields]
  );

  // Handle mapping change for a column.
  //
  // sl-8fwi.8 (#64 fix): The mapping math now lives in the pure
  // `applyMappingChange` helper so the spec can drive the same code
  // path the user does. Pre-fix the only OutputMapping coverage was
  // the auto-match suggestion pipeline — the manual writer path
  // (this callback) had zero tests, hiding regressions in clear /
  // matchConfig preservation / displayName-fallback corner cases.
  const handleMappingChange = useCallback(
    (columnId: string, responseField: string) => {
      const column = targetColumns.find(column => column.id === columnId);
      if (column && responseField && isFileIndexProtectedColumn(column)) {
        notify.warning({
          title: 'System column',
          message:
            'Files database system columns are read-only. Create a user column for automation output.',
        });
        return;
      }
      if (
        responseField &&
        !mappings.some(m => m.targetColumnId === columnId) &&
        mappings.length >= AUTOMATION_MAPPING_LIMIT
      ) {
        notify.warning({
          title: 'Mapping limit reached',
          message: `Automations support up to ${AUTOMATION_MAPPING_LIMIT} output mappings.`,
        });
        return;
      }
      onMappingsChange(
        applyMappingChange({
          prev: mappings,
          columnId,
          responseField,
          tableId,
          responseFields,
          getDisplayName,
        })
      );
    },
    [
      getDisplayName,
      isFileIndexProtectedColumn,
      mappings,
      onMappingsChange,
      responseFields,
      tableId,
      targetColumns,
    ]
  );

  // sl-vb5r: Handle fixed value selection for select columns
  const handleFixedValueSelect = useCallback(
    (columnId: string, optionId: string) => {
      const column = targetColumns.find(column => column.id === columnId);
      if (column && isFileIndexProtectedColumn(column)) {
        notify.warning({
          title: 'System column',
          message:
            'Files database system columns are read-only. Create a user column for automation output.',
        });
        return;
      }
      const filteredMappings = mappings.filter(
        m => m.targetColumnId !== columnId
      );
      if (
        !mappings.some(m => m.targetColumnId === columnId) &&
        filteredMappings.length >= AUTOMATION_MAPPING_LIMIT
      ) {
        notify.warning({
          title: 'Mapping limit reached',
          message: `Automations support up to ${AUTOMATION_MAPPING_LIMIT} output mappings.`,
        });
        return;
      }
      if (tableId) {
        filteredMappings.push({
          tableId,
          targetColumnId: columnId,
          responseField: '',
          fixedValue: optionId,
        });
      }
      onMappingsChange(filteredMappings);
    },
    [
      isFileIndexProtectedColumn,
      mappings,
      onMappingsChange,
      tableId,
      targetColumns,
    ]
  );

  // sl-mau5: Handle relation lookup toggle — creates/removes MatchConfig
  const handleRelationLookupToggle = useCallback(
    (columnId: string, enabled: boolean) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping) return;

      if (enabled) {
        // Create MatchConfig for this relation lookup
        const column = targetColumns.find(c => c.id === columnId);
        const targetDbId = column?.targetDatabaseId;
        if (!targetDbId) return;

        const mcId = generateId();
        const newMc: MatchConfigProps = {
          id: mcId,
          targetTableId: targetDbId,
          multiMatchBehavior: 'ask',
          keys: [
            {
              columnId: 'title',
              sourceField: mapping.responseField,
              matchMode: 'exact',
            },
          ],
        };
        onMatchConfigsChange([...matchConfigs, newMc]);
        // Set matchConfigId on the mapping
        const updatedMappings = mappings.map(m =>
          m.targetColumnId === columnId ? { ...m, matchConfigId: mcId } : m
        );
        onMappingsChange(updatedMappings);
      } else {
        // Remove MatchConfig and clear reference
        const mcId = mapping.matchConfigId;
        if (mcId) {
          onMatchConfigsChange(matchConfigs.filter(mc => mc.id !== mcId));
        }
        const updatedMappings = mappings.map(m =>
          m.targetColumnId === columnId ? { ...m, matchConfigId: undefined } : m
        );
        onMappingsChange(updatedMappings);
      }
    },
    [
      mappings,
      matchConfigs,
      targetColumns,
      onMappingsChange,
      onMatchConfigsChange,
    ]
  );

  // sl-mau5: Handle relation match mode change — updates MatchConfig key
  const handleMatchModeChange = useCallback(
    (columnId: string, mode: RelationMatchMode) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId) return;
      const updatedConfigs = matchConfigs.map(mc => {
        if (mc.id === mapping.matchConfigId && mc.keys.length > 0) {
          const updatedKeys = [...mc.keys];
          updatedKeys[0] = { ...updatedKeys[0], matchMode: mode };
          return { ...mc, keys: updatedKeys };
        }
        return mc;
      });
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  // sl-mau5: Handle match key toggle — adds/removes key from row MatchConfig
  const handleMatchKeyToggle = useCallback(
    (columnId: string, enabled: boolean) => {
      if (!onMatchConfigsChange || !onOutputTablesChange || !tableId) return;

      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping) return;

      const existingMc = getRowMatchConfig();

      if (enabled) {
        const newKey = {
          columnId: mapping.targetColumnId,
          sourceField: mapping.responseField,
          matchMode: 'auto' as string,
        };

        if (existingMc) {
          // Add key to existing MatchConfig
          const updatedConfigs = matchConfigs.map(mc =>
            mc.id === existingMc.id ? { ...mc, keys: [...mc.keys, newKey] } : mc
          );
          onMatchConfigsChange(updatedConfigs);
        } else {
          // Create new MatchConfig for row matching
          const mcId = generateId();
          const newMc: MatchConfigProps = {
            id: mcId,
            targetTableId: tableId,
            multiMatchBehavior: 'fail',
            notFoundBehavior: isFileIndexTable ? 'fail' : 'create',
            keys: [newKey],
          };
          onMatchConfigsChange([...matchConfigs, newMc]);
          // Set matchConfigId on OutputTable
          const updatedTables = outputTables.map(t =>
            t.tableId === tableId ? { ...t, matchConfigId: mcId } : t
          );
          onOutputTablesChange(updatedTables);
        }
      } else {
        // Validate before disabling
        if (
          tableId &&
          sourceTableId &&
          tableId === sourceTableId &&
          existingMc &&
          existingMc.keys.length <= 1
        ) {
          notify.warning({
            title:
              t[
                'com.streamlain.streamlain.validation.cannot-remove-match-key'
              ](),
            message:
              t[
                'com.streamlain.streamlain.validation.match-key-required-short'
              ](),
          });
          return;
        }

        if (existingMc) {
          const remainingKeys = existingMc.keys.filter(
            k => k.columnId !== columnId
          );
          if (remainingKeys.length === 0) {
            // Remove MatchConfig entirely and clear OutputTable reference
            onMatchConfigsChange(
              matchConfigs.filter(mc => mc.id !== existingMc.id)
            );
            const updatedTables = outputTables.map(t =>
              t.tableId === tableId ? { ...t, matchConfigId: undefined } : t
            );
            onOutputTablesChange(updatedTables);
          } else {
            // Just remove the key
            const updatedConfigs = matchConfigs.map(mc =>
              mc.id === existingMc.id ? { ...mc, keys: remainingKeys } : mc
            );
            onMatchConfigsChange(updatedConfigs);
          }
        }
      }
    },
    [
      mappings,
      matchConfigs,
      outputTables,
      tableId,
      sourceTableId,
      isFileIndexTable,
      t,
      getRowMatchConfig,
      onMatchConfigsChange,
      onOutputTablesChange,
    ]
  );

  // sl-mau5: Handle match key mode change — updates key in row MatchConfig
  const handleMatchKeyModeChange = useCallback(
    (columnId: string, mode: TargetMatchMode) => {
      if (!onMatchConfigsChange) return;
      const mc = getRowMatchConfig();
      if (!mc) return;
      const updatedConfigs = matchConfigs.map(c => {
        if (c.id === mc.id) {
          return {
            ...c,
            keys: c.keys.map(k =>
              k.columnId === columnId ? { ...k, matchMode: mode } : k
            ),
          };
        }
        return c;
      });
      onMatchConfigsChange(updatedConfigs);
    },
    [matchConfigs, getRowMatchConfig, onMatchConfigsChange]
  );

  // sl-mau5: Handle "if not found" behavior change — updates row MatchConfig
  const handleNotFoundBehaviorChange = useCallback(
    (_columnId: string, behavior: 'fail' | 'create') => {
      if (!onMatchConfigsChange) return;

      if (isFileIndexTable && behavior === 'create') {
        notify.warning({
          title: 'New rows disabled',
          message: fileIndexOutputHint,
        });
        return;
      }

      // Block "create" when input table == output table
      if (
        behavior === 'create' &&
        tableId &&
        sourceTableId &&
        tableId === sourceTableId
      ) {
        notify.warning({
          title:
            t['com.streamlain.streamlain.validation.cannot-select-create'](),
          message: t['com.streamlain.streamlain.validation.fail-required'](),
        });
        return;
      }

      const mc = getRowMatchConfig();
      if (!mc) return;
      const updatedConfigs = matchConfigs.map(c =>
        c.id === mc.id ? { ...c, notFoundBehavior: behavior } : c
      );
      onMatchConfigsChange(updatedConfigs);
    },
    [
      matchConfigs,
      fileIndexOutputHint,
      isFileIndexTable,
      tableId,
      sourceTableId,
      t,
      getRowMatchConfig,
      onMatchConfigsChange,
    ]
  );

  // sl-mau5: Handle "if multiple found" behavior change
  const handleMultiMatchBehaviorChange = useCallback(
    (columnId: string, behavior: MultiMatchBehavior) => {
      if (!onMatchConfigsChange) return;

      // Check if this is for a relation lookup or row matching
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      const relationMcId = mapping?.matchConfigId;

      if (relationMcId) {
        // Relation lookup: update that specific MatchConfig
        const updatedConfigs = matchConfigs.map(mc =>
          mc.id === relationMcId ? { ...mc, multiMatchBehavior: behavior } : mc
        );
        onMatchConfigsChange(updatedConfigs);
      } else {
        // Row matching: update the row MatchConfig
        const mc = getRowMatchConfig();
        if (!mc) return;
        const updatedConfigs = matchConfigs.map(c =>
          c.id === mc.id ? { ...c, multiMatchBehavior: behavior } : c
        );
        onMatchConfigsChange(updatedConfigs);
      }
    },
    [mappings, matchConfigs, getRowMatchConfig, onMatchConfigsChange]
  );

  // sl-tah4.4: Handle steal behavior change — updates MatchConfig for relation lookup
  const handleStealBehaviorChange = useCallback(
    (columnId: string, behavior: StealBehavior) => {
      if (!onMatchConfigsChange) return;
      const mapping = mappings.find(m => m.targetColumnId === columnId);
      if (!mapping?.matchConfigId) return;
      const updatedConfigs = matchConfigs.map(mc =>
        mc.id === mapping.matchConfigId
          ? { ...mc, stealBehavior: behavior }
          : mc
      );
      onMatchConfigsChange(updatedConfigs);
    },
    [mappings, matchConfigs, onMatchConfigsChange]
  );

  // Check what data source we have for dropdown functionality
  const hasFieldSuggestions = responseFields.length > 0;

  // Count mapped fields for badge
  const mappedCount = mappings.length;

  // Badge for mapped count
  const badge =
    mappedCount > 0
      ? { label: String(mappedCount), status: 'active' as const }
      : undefined;

  if (targetColumns.length === 0) {
    const emptyContent = (
      <SectionEmpty>
        {t['com.streamlain.streamlain.output.select-target-first']()}
      </SectionEmpty>
    );

    if (noWrapper) {
      return emptyContent;
    }

    return (
      <CollapsibleSection
        title={t['com.streamlain.streamlain.output.field-mapping']()}
        defaultCollapsed
      >
        {emptyContent}
      </CollapsibleSection>
    );
  }

  // Main content - extracted so we can optionally skip the wrapper
  const gridContent = (
    <>
      {/* Auto-Match Dialog */}
      {isAutoMatchOpen && tableId
        ? (() => {
            const autoMatchTableId = tableId;
            return (
              <AutoMatchDialog
                open={isAutoMatchOpen}
                onClose={() => setIsAutoMatchOpen(false)}
                sourceFields={responseFields}
                targetFields={targetColumns.map(c => ({
                  id: c.id,
                  name: c.name,
                  type: c.type,
                }))}
                existingTargetIds={new Set(mappings.map(m => m.targetColumnId))}
                sourceLabel={t[
                  'com.streamlain.streamlain.mapping.response-field'
                ]()}
                targetLabel={t[
                  'com.streamlain.streamlain.mapping.target-column'
                ]()}
                onApply={suggestions => {
                  const selectedIds = new Set(
                    suggestions.map(s => s.targetColumnId)
                  );
                  const preserved = mappings.filter(
                    m => !selectedIds.has(m.targetColumnId)
                  );
                  const availableSlots = Math.max(
                    0,
                    AUTOMATION_MAPPING_LIMIT - preserved.length
                  );
                  const limitedSuggestions = suggestions.slice(
                    0,
                    availableSlots
                  );
                  if (limitedSuggestions.length < suggestions.length) {
                    notify.warning({
                      title: 'Mapping limit reached',
                      message: `Automations support up to ${AUTOMATION_MAPPING_LIMIT} output mappings.`,
                    });
                  }
                  const newMappings = limitedSuggestions.map(s => ({
                    tableId: autoMatchTableId,
                    targetColumnId: s.targetColumnId,
                    responseField: s.responseField,
                    responseFieldName: s.responseFieldName,
                    responseFieldType: responseFields.find(
                      f => f.path === s.responseField
                    )?.type,
                  }));
                  onMappingsChange([...preserved, ...newMappings]);
                }}
              />
            );
          })()
        : null}
      {/* Info Banner - only show when not using noWrapper (parent handles it) */}
      {!noWrapper && !hasFieldSuggestions && (
        <SectionNotice icon={<InformationIcon />}>
          <span>{t['com.streamlain.streamlain.output.discover-fields']()}</span>
        </SectionNotice>
      )}
      {/* Mapping Grid */}
      <SectionStack>
        {/* Header */}
        <SectionMappingHeader columns={OUTPUT_MAPPING_GRID_COLUMNS}>
          <span>{t['com.streamlain.streamlain.mapping.response-field']()}</span>
          <span>{t['com.streamlain.streamlain.mapping.target-column']()}</span>
          <span></span>
          {/* sl-240a: Key column header with link icon - rightmost */}
          <span
            title={t['com.streamlain.streamlain.mapping.match-key-title']()}
            style={{
              display: 'flex',
              justifyContent: 'center',
              cursor: 'help',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </span>
        </SectionMappingHeader>

        {isFileIndexTable && !getRowMatchConfig() && (
          <SectionNotice>{fileIndexOutputHint}</SectionNotice>
        )}

        {/* Mapping Rows */}
        {targetColumns.map(column => {
          const mappedField = getMappingForColumn(column.id);
          const fullMapping = getFullMappingForColumn(column.id);
          const fixedValue = fullMapping?.fixedValue;
          const fieldInfo = mappedField
            ? getResponseFieldInfo(mappedField)
            : null;
          // E018-S008-T011: Detect orphaned mappings (field no longer in response)
          const isOrphanedMapping =
            mappedField && hasFieldSuggestions && !fieldInfo;
          const compatibility =
            fieldInfo && hasFieldSuggestions
              ? getTypeCompatibility(fieldInfo.type, column.type)
              : null;
          const fileIndexProtectedColumn = isFileIndexProtectedColumn(column);

          return (
            <SectionMappingRow
              key={column.id}
              columns={OUTPUT_MAPPING_GRID_COLUMNS}
            >
              {/* Response Field - Dropdown when response or schema exists, static display otherwise */}
              {fileIndexProtectedColumn ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '13px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--streamlain-hover-color)',
                    color: 'var(--streamlain-text-secondary-color)',
                    opacity: 0.7,
                    cursor: 'not-allowed',
                  }}
                  title="Files database system columns are read-only."
                >
                  System column
                </div>
              ) : hasFieldSuggestions ? (
                <TypedFieldSelect
                  value={
                    mappedField || (fixedValue ? `fixed:${fixedValue}` : '')
                  }
                  groups={(() => {
                    // Prepend Fixed Values group for select columns
                    const allGroups = [...responseFieldGroups];
                    const selectOptions =
                      (column.data?.options as Array<{
                        id: string;
                        value: string;
                      }>) ?? [];
                    if (column.type === 'select' && selectOptions.length > 0) {
                      allGroups.unshift({
                        id: 'fixed-values',
                        label:
                          t['com.streamlain.streamlain.output.group-fixed'](),
                        options: selectOptions.map(opt => ({
                          id: `fixed:${opt.id}`,
                          label: opt.value,
                          type: 'select',
                        })),
                      });
                    }
                    return allGroups;
                  })()}
                  onChange={optionId => {
                    if (!optionId) {
                      // Clear both field and fixed value
                      handleMappingChange(column.id, '');
                    } else if (optionId.startsWith('fixed:')) {
                      handleFixedValueSelect(
                        column.id,
                        optionId.slice('fixed:'.length)
                      );
                    } else {
                      handleMappingChange(column.id, optionId);
                    }
                  }}
                  placeholder={t[
                    'com.streamlain.streamlain.mapping.select-field'
                  ]()}
                  targetType={column.type}
                  searchable
                  searchPlaceholder={t[
                    'com.streamlain.streamlain.mapping.search-fields'
                  ]()}
                />
              ) : (
                /* Static display when no response - shows saved mapping */
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '13px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--streamlain-hover-color)',
                    opacity: 0.7,
                  }}
                >
                  {mappedField ? (
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getDisplayName(mappedField)}
                    </span>
                  ) : fixedValue ? (
                    <span
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: '9px',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          backgroundColor: 'var(--streamlain-primary-color)',
                          color: 'white',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        FIXED
                      </span>
                      <span
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {resolveOptionLabel(column, fixedValue)}
                      </span>
                    </span>
                  ) : (
                    <span style={{ flex: 1, opacity: 0.5 }}>
                      {t['com.streamlain.streamlain.mapping.not-mapped']()}
                    </span>
                  )}
                </div>
              )}

              {/* Target Column */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.name}
                </span>
                <TypeChip
                  type={column.type}
                  small
                  style={{ marginLeft: 'auto' }}
                />
              </div>

              {/* Compatibility Indicator */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isOrphanedMapping && (
                  <span
                    title={t[
                      'com.streamlain.streamlain.mapping.orphaned-field'
                    ]()}
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: '#9ca3af',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    ?
                  </span>
                )}
                {compatibility && (
                  <span
                    title={
                      compatibility === 'ok'
                        ? t['com.streamlain.streamlain.output.compat-ok']()
                        : compatibility === 'error'
                          ? t['com.streamlain.streamlain.output.compat-error']()
                          : COMPAT_CONFIG[compatibility].title
                    }
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: COMPAT_CONFIG[compatibility].color,
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    {COMPAT_CONFIG[compatibility].label}
                  </span>
                )}
              </div>

              {/* sl-240a: Match Key Checkbox - rightmost column, only show when mapping exists */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                title={t[
                  'com.streamlain.streamlain.mapping.match-key-tooltip'
                ]()}
              >
                {mappedField && !fileIndexProtectedColumn && (
                  <Checkbox
                    checked={isColumnMatchKey(column.id)}
                    onChange={(_event: unknown, checked: boolean) =>
                      handleMatchKeyToggle(column.id, checked)
                    }
                  />
                )}
              </div>

              {/* sl-mau5: Match Key Config Row — reads from MatchConfig */}
              {isColumnMatchKey(column.id) &&
                (() => {
                  const mc = getRowMatchConfig();
                  const key = mc?.keys.find(k => k.columnId === column.id);
                  return (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        padding: '8px 10px',
                        marginTop: '4px',
                        backgroundColor:
                          'var(--streamlain-background-secondary-color)',
                        borderRadius: '6px',
                        fontSize: '12px',
                      }}
                    >
                      <MatchConfigEditor
                        infoText={t[
                          'com.streamlain.streamlain.output.match-info'
                        ]()}
                        matchMode={key?.matchMode ?? 'auto'}
                        matchModeOptions={MATCH_MODE_WITH_AUTO_OPTIONS}
                        onMatchModeChange={mode =>
                          handleMatchKeyModeChange(
                            column.id,
                            mode as TargetMatchMode
                          )
                        }
                        notFoundBehavior={mc?.notFoundBehavior ?? 'create'}
                        onNotFoundBehaviorChange={behavior =>
                          handleNotFoundBehaviorChange(column.id, behavior)
                        }
                        disableCreate={
                          isFileIndexTable ||
                          !!(
                            tableId &&
                            sourceTableId &&
                            tableId === sourceTableId
                          )
                        }
                        disableCreateReason={
                          isFileIndexTable ? fileIndexOutputHint : undefined
                        }
                        multiMatchBehavior={mc?.multiMatchBehavior ?? 'fail'}
                        onMultiMatchBehaviorChange={behavior =>
                          handleMultiMatchBehaviorChange(column.id, behavior)
                        }
                      />
                    </div>
                  );
                })()}

              {/* sl-mau5: Relation Lookup Row - reads from MatchConfig */}
              {isRelationColumn(column.type) &&
                mappedField &&
                (() => {
                  const targetInfo = getRelationTargetInfo(column);
                  const currentMapping = getFullMappingForColumn(column.id);
                  const relationMc = getRelationMatchConfig(column.id);
                  const hasRelationLookup = !!currentMapping?.matchConfigId;
                  return (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        padding: '8px 10px',
                        marginTop: '4px',
                        backgroundColor:
                          'var(--streamlain-background-secondary-color)',
                        borderRadius: '6px',
                        fontSize: '12px',
                      }}
                    >
                      {/* Target table name + click to open */}
                      {targetInfo && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <span style={{ fontSize: '11px', opacity: 0.6 }}>
                            Target:
                          </span>
                          <button
                            onClick={() => handleOpenRelationTarget(column)}
                            title={`Open "${targetInfo.tableName}" in fullscreen`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 500,
                              borderRadius: '4px',
                              border:
                                '1px solid var(--streamlain-border-color)',
                              backgroundColor:
                                'var(--streamlain-background-primary-color)',
                              color: 'var(--streamlain-primary-color)',
                              cursor: 'pointer',
                              textDecoration: 'none',
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            {targetInfo.tableName}
                          </button>
                          {/* Cardinality chip: shows the link type so user understands steal behavior */}
                          <Tooltip
                            content={
                              column.inverseCardinality === 'one'
                                ? 'Each row in the target can link to only one row here. Overwrites may occur.'
                                : 'Each row in the target can link to many rows here. No overwrite conflicts.'
                            }
                          >
                            <span
                              style={{
                                padding: '1px 6px',
                                fontSize: '9px',
                                fontWeight: 600,
                                borderRadius: '3px',
                                backgroundColor:
                                  column.inverseCardinality === 'one'
                                    ? 'var(--streamlain-warning-color, #e8a33d)'
                                    : 'var(--streamlain-primary-color)',
                                color: 'white',
                                cursor: 'help',
                                letterSpacing: '0.3px',
                              }}
                            >
                              {column.cardinality === 'one' ? '1' : 'N'}:
                              {column.inverseCardinality === 'one' ? '1' : 'N'}
                            </span>
                          </Tooltip>
                        </div>
                      )}

                      {/* Smart Lookup toggle + tooltip row */}
                      <div
                        data-coachmark-target="relation-lookup-toggle"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <Checkbox
                            checked={hasRelationLookup}
                            onChange={(_event: unknown, checked: boolean) =>
                              handleRelationLookupToggle(column.id, checked)
                            }
                          />
                          <span style={{ fontWeight: 500 }}>
                            {t[
                              'com.streamlain.streamlain.mapping.smart-lookup'
                            ]()}
                          </span>
                        </label>

                        <Tooltip
                          content={t[
                            'com.streamlain.streamlain.mapping.smart-lookup-tooltip'
                          ]()}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'help',
                              opacity: 0.5,
                            }}
                          >
                            <InformationIcon
                              style={{ width: '14px', height: '14px' }}
                            />
                          </span>
                        </Tooltip>
                      </div>

                      {/* sl-mau5: Match config — reads from MatchConfig */}
                      {hasRelationLookup && relationMc && (
                        <MatchConfigEditor
                          matchMode={relationMc.keys[0]?.matchMode ?? 'exact'}
                          matchModeOptions={MATCH_MODE_WITH_AUTO_OPTIONS}
                          onMatchModeChange={mode =>
                            handleMatchModeChange(
                              column.id,
                              mode as RelationMatchMode
                            )
                          }
                          columns={targetInfo?.columns}
                          selectedColumnId={
                            relationMc.keys[0]?.columnId === 'title'
                              ? undefined
                              : relationMc.keys[0]?.columnId
                          }
                          onColumnChange={colId =>
                            handleLookupMatchColumnChange(column.id, colId)
                          }
                          multiMatchBehavior={
                            relationMc.multiMatchBehavior ?? 'ask'
                          }
                          onMultiMatchBehaviorChange={behavior =>
                            handleMultiMatchBehaviorChange(column.id, behavior)
                          }
                          stealBehavior={
                            column.inverseCardinality === 'one'
                              ? (relationMc.stealBehavior ?? 'fail')
                              : undefined
                          }
                          onStealBehaviorChange={
                            column.inverseCardinality === 'one'
                              ? behavior =>
                                  handleStealBehaviorChange(column.id, behavior)
                              : undefined
                          }
                        />
                      )}
                    </div>
                  );
                })()}
            </SectionMappingRow>
          );
        })}
      </SectionStack>

      {/* Auto-Map button */}
      {hasFieldSuggestions && targetColumns.length > 0 && (
        <button
          onClick={() => setIsAutoMatchOpen(true)}
          style={{
            width: '100%',
            padding: '6px 12px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: 'var(--streamlain-primary-color)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            marginTop: '4px',
          }}
        >
          {t['com.streamlain.streamlain.automap.button']()}
        </button>
      )}
    </>
  );

  // If noWrapper is set, return content directly (used in multi-table grouped view)
  if (noWrapper) {
    return gridContent;
  }

  // Default: wrap in CollapsibleSection
  return (
    <CollapsibleSection
      title={t['com.streamlain.streamlain.output.field-mapping']()}
      subtitle={t['com.streamlain.streamlain.output.field-mapping-desc']()}
      badge={badge}
      defaultCollapsed
    >
      {gridContent}
    </CollapsibleSection>
  );
};
